// 11-slide deck — punchy, tailored, action-oriented.
// Routing by prospect archetype (see industryPack.js):
//   1B (PVS, Sloan, Impulse Alarm, AAA) — Legal slide replaces "The Offer" — primary pitch IS CompliSun Legal.
//   2A/2B (law firms) — Legal slide inserted before Build-my-Prototype.
//   6 (GLEP) — Legal slide appended after Contact as bonus (combined pitch).
//   3 (Cross River, TAB Bank, Celtic) — no Legal slide.
//   Everyone else (1A, 4, default) — base 11-slide deck.
import { S_Title } from "./sections/S01_Title";
import { S_Problem } from "./sections/S02_Problem";
import { S_Guide } from "./sections/S05_Guide";
import { S_HowItWorks } from "./sections/S06b_HowItWorks";
import { S_ClosedSystems } from "./sections/S07_ClosedSystems";
import { S_Platform } from "./sections/S11c_Platform";
import { S_TryIt } from "./sections/S10b_TryIt";
import { S_Team } from "./sections/S10_Team";
import { S_Trusted } from "./sections/S11_Trusted";
import { S_Enterprise } from "./sections/S13_Enterprise";
import { S_Contact } from "./sections/S15_Contact";
import { S_LegalHelp } from "./sections/S12_LegalHelp";
import { isLawFirm, isLegalCorporate, isLegalPrimary, isCombined, packFor as packForProspect } from "@/lib/industryPack";

export {
  S_Title, S_Problem, S_Guide, S_HowItWorks, S_ClosedSystems,
  S_Platform, S_TryIt, S_Team, S_Trusted, S_Enterprise, S_Contact, S_LegalHelp,
};

const BASE = [
  { c: S_Title,         t: "Welcome",            group: "Setup" },
  { c: S_Problem,       t: "The Problem",        group: "Setup" },
  { c: S_Guide,         t: "Your Guide",         group: "Setup" },
  { c: S_HowItWorks,    t: "How It Works",       group: "How We Work" },
  { c: S_ClosedSystems, t: "Closed Systems",     group: "How We Work" },
  { c: S_Platform,      t: "The Platform",       group: "Tech" },
  { c: S_TryIt,         t: "Try It Yourself",    group: "Tech" },
  { c: S_Team,          t: "The Team",           group: "Proof" },
  { c: S_Trusted,       t: "Trusted By",         group: "Proof" },
  { c: S_Enterprise,    t: "The Offer",          group: "Engage" },
  { c: S_Contact,       t: "Build my Prototype", group: "Engage" },
];

const LEGAL_ENTRY = { c: S_LegalHelp, t: "CompliSun Legal PLC", group: "Engage" };
const LAWFIRM_OFFER_ENTRY = { c: S_LegalHelp, t: "The Offer", group: "Engage" };
const LEGAL_BONUS_ENTRY = { c: S_LegalHelp, t: "Need Legal Help?", group: "Bonus" };

/** Returns the ordered list of {component, title, group} for this prospect. */
export function deckFor(prospect) {
  // 1B — Legal Corporate: REPLACE "The Offer" with the Legal slide. Primary pitch IS legal.
  if (isLegalCorporate(prospect)) {
    // Sloan / PVS Chemicals / Impulse Alarm / AAA — primary pitch is CompliSun Legal PLC (outside counsel).
    // Platform + Try It Yourself reframe to "and here's the tech your in-house team gets too".
    // Order: Welcome → Problem → Guide → Legal PLC → How It Works → Closed Systems → Team → Trusted → Platform → Try It → Build.
    if (isLegalPrimary(prospect)) {
      // Override groups so the sidebar reads top-to-bottom in slide order:
      //   Setup · Offer · How We Work · Proof · Tech · Engage
      return [
        BASE[0],                                                         // Welcome           · Setup
        BASE[1],                                                         // The Problem       · Setup
        BASE[2],                                                         // Your Guide        · Setup
        { ...LEGAL_ENTRY, group: "Offer" },                              // CompliSun Legal PLC · Offer  ← primary
        { ...BASE[3], group: "How We Work" },                            // How It Works      · How We Work
        { ...BASE[4], group: "How We Work" },                            // Closed Systems    · How We Work
        { ...BASE[7], group: "Proof" },                                  // The Team          · Proof
        { ...BASE[8], group: "Proof" },                                  // Trusted By        · Proof
        { ...BASE[5], group: "Tech" },                                   // The Platform      · Tech (secondary)
        { ...BASE[6], group: "Tech" },                                   // Try It Yourself   · Tech (secondary)
        { ...BASE[10], group: "Engage" },                                // Build my Prototype · Engage
      ];
    }

    return [
      ...BASE.slice(0, 9),                 // up through "Trusted By"
      LEGAL_ENTRY,                          // Legal in place of "The Offer"
      BASE[10],                             // Build my Prototype
    ];
  }
  // 2A/2B — Law firm channel:
  //   • 2A (Dykema, Chapman, Mavacy) — drop "Try It Yourself" + "Trusted By"; Legal slide replaces "The Offer".
  //   • 2B (Brom Law) — keep base flow; Legal slide inserted before Contact.
  if (isLawFirm(prospect)) {
    const pack = packForProspect(prospect);
    if (pack.suppressTryIt || pack.suppressTrusted) {
      const base = BASE.filter((s, i) => {
        if (pack.suppressTryIt && i === 6) return false;     // "Try It Yourself"
        if (pack.suppressTrusted && i === 8) return false;   // "Trusted By"
        return true;
      });
      // For 2A the new "The Offer" (LawFirmOffer) REPLACES the existing S_Enterprise "The Offer".
      // Result: drop the trailing Contact + Enterprise, append LAWFIRM_OFFER_ENTRY, then Contact.
      return [
        ...base.slice(0, -2),
        LAWFIRM_OFFER_ENTRY,
        base[base.length - 1],
      ];
    }
    return [
      ...BASE.slice(0, -1),
      LEGAL_ENTRY,
      BASE[BASE.length - 1],
    ];
  }
  // 6 — Combined (GLEP): Legal slide appended after Contact as a bonus.
  if (isCombined(prospect)) {
    return [...BASE, LEGAL_BONUS_ENTRY];
  }
  // 1A, 3, 4, default — base 11-slide deck (no Legal slide).
  return BASE;
}

// Back-compat exports for any caller still importing SLIDES (no prospect-awareness).
export const SLIDES = BASE.map((s) => s.c);
